import React, { useMemo, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinearProgress from '@mui/material/LinearProgress';
import SearchIcon from '@mui/icons-material/Search';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface ResearchStep {
  step_id: number;
  status: string;
  query: string;
  searches_done?: number;
  pages_visited?: number;
  tool_calls_done?: number;
  [key: string]: unknown;
}

interface DeepResearchProgressProps {
  phase: string;
  progress: number;
  steps: Record<number, ResearchStep>;
  inputTokens?: number;
  outputTokens?: number;
}

const PHASE_LABELS: Record<string, string> = {
  planning: 'Criando plano de pesquisa',
  researching: 'Pesquisando',
  reviewing: 'Verificando completude',
  synthesizing: 'Escrevendo relatório',
  done: 'Pesquisa concluída',
};

const formatTokenCount = (n: number): string => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toString();
};

const DeepResearchProgress: React.FC<DeepResearchProgressProps> = ({
  phase,
  progress,
  steps,
  inputTokens = 0,
  outputTokens = 0,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { totalSearches, totalPages, totalToolCalls } = useMemo(() => {
    let searches = 0;
    let pages = 0;
    let toolCalls = 0;
    for (const stepData of Object.values(steps)) {
      searches += stepData.searches_done || 0;
      pages += stepData.pages_visited || 0;
      toolCalls += stepData.tool_calls_done || 0;
    }
    return { totalSearches: searches, totalPages: pages, totalToolCalls: toolCalls };
  }, [steps]);

  const isDone = phase === 'done';
  const phaseLabel = PHASE_LABELS[phase] || phase;
  const hasStats = totalSearches > 0 || totalPages > 0 || totalToolCalls > 0 || inputTokens > 0;

  return (
    <Row>
      <Header
        $clickable
        onClick={() => setExpanded(v => !v)}
      >
        <IconWrap $pending={!isDone}>
          {isDone ? (
            <CheckCircleIcon style={{ fontSize: 15, color: '#4caf50' }} />
          ) : (
            <TravelExploreIcon style={{ fontSize: 15 }} />
          )}
        </IconWrap>
        <Verb>Pesquisa aprofundada{isDone ? '' : ` — ${phaseLabel.toLowerCase()}`}</Verb>
        {hasStats && (
          <Stats>
            {totalSearches > 0 && <span><SearchIcon style={{ fontSize: 12 }} /> {totalSearches}</span>}
            {totalPages > 0 && <span><LanguageIcon style={{ fontSize: 12 }} /> {totalPages}</span>}
            {totalToolCalls > 0 && <span><StorageIcon style={{ fontSize: 12 }} /> {totalToolCalls}</span>}
            {(inputTokens > 0 || outputTokens > 0) && (
              <>
                <span>↑ {formatTokenCount(inputTokens)}</span>
                <span>↓ {formatTokenCount(outputTokens)}</span>
              </>
            )}
          </Stats>
        )}
        <ChevronWrap>
          {expanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
        </ChevronWrap>
      </Header>
      {!isDone && (
        <ProgressLine>
          <LinearProgress variant="determinate" value={Math.round(progress * 100)} />
        </ProgressLine>
      )}
      {!isDone && <Hint>A pesquisa aprofundada pode levar alguns minutos.</Hint>}
      {expanded && (
        <Details>
          <DetailLine>
            <DetailIcon><SearchIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{totalSearches}</strong> buscas</DetailLabel>
            <DetailDesc>consultas feitas a buscadores na web</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon><LanguageIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{totalPages}</strong> páginas</DetailLabel>
            <DetailDesc>páginas abertas e lidas em profundidade</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon><StorageIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{totalToolCalls}</strong> consultas</DetailLabel>
            <DetailDesc>chamadas a bases internas e ferramentas auxiliares</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon>↑</DetailIcon>
            <DetailLabel><strong>{formatTokenCount(inputTokens)}</strong> tokens de entrada</DetailLabel>
            <DetailDesc>texto enviado ao modelo durante a pesquisa</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon>↓</DetailIcon>
            <DetailLabel><strong>{formatTokenCount(outputTokens)}</strong> tokens de saída</DetailLabel>
            <DetailDesc>texto gerado pelo modelo durante a pesquisa</DetailDesc>
          </DetailLine>
        </Details>
      )}
    </Row>
  );
};

export default DeepResearchProgress;

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 0.95; }
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin: 4px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.font_color || '#666'};
  opacity: 0.85;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  user-select: none;
  flex-wrap: wrap;
`;

const IconWrap = styled.span<{ $pending: boolean }>`
  display: inline-flex;
  align-items: center;
  opacity: 0.75;
  ${({ $pending }) => $pending && css`animation: ${pulse} 1.6s ease-in-out infinite;`}
`;

const Verb = styled.span`
  font-weight: 500;
`;

const Stats = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  opacity: 0.7;
  font-size: 0.78rem;
  & > span { display: inline-flex; align-items: center; gap: 3px; }
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.55;
`;

const ProgressLine = styled.div`
  margin: 6px 0 2px;
  max-width: 480px;
  && .MuiLinearProgress-root {
    height: 3px;
    border-radius: 2px;
    background: ${({ theme }) => theme.borderGrey || '#e0e0e0'};
  }
  && .MuiLinearProgress-bar {
    border-radius: 2px;
    background: ${({ theme }) => theme.send_button_bg || '#1976d2'};
  }
`;

const Hint = styled.span`
  font-size: 0.72rem;
  opacity: 0.45;
  color: ${({ theme }) => theme.font_color || '#999'};
  margin-top: 4px;
`;

const Details = styled.div`
  margin-top: 8px;
  margin-left: 22px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.78rem;
  opacity: 0.8;
  line-height: 1.5;
`;

const DetailIcon = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.7;
  width: 16px;
  flex-shrink: 0;
`;

const DetailLabel = styled.span`
  min-width: 120px;
  flex-shrink: 0;
`;

const DetailDesc = styled.span`
  opacity: 0.65;
  word-break: break-word;
`;
